import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import './source-css/theme.css';
import './source-css/app.css';
// After app.css so it can override it: the platform's Material 3 chip and button language
// (CLAUDE.md §1.9), the same file App Rationalization loads, re-scoped.
import './source-css/chips.css';
import './PpScreen.css';
// After everything else: Program Planning on the Decision Workspace design (owner ruling
// 2026-10-21). Values come from the kit tokens via wsCssVars(); see workspace/pp-workspace.css.
import './workspace/pp-workspace.css';
import { ThemeProvider } from '@mui/material/styles';
import { WorkspaceThemeProvider } from '@platform/ui-kit/workspace';
import { wsCssVars } from './workspace/ws';

const WS_VARS = wsCssVars();

/**
 * Kit dialogs and sheets inside Program Planning:
 *  - render IN PLACE (no portal), so they sit inside `#pp-ws` and take the module's
 *    design layer and `--ws-*` token variables like everything else on the screen;
 *  - open above the shell's app bar and the cloned screens' own overlays (1500).
 * MUI's defaults are a portal on <body> and 1200/1300, which put the panel header under
 * the app bar and its contents outside the layer.
 */
type ThemeLike = { zIndex?: Record<string, number>; components?: Record<string, { defaultProps?: object }> };
const IN_PLACE = ['MuiModal', 'MuiDrawer', 'MuiDialog', 'MuiPopover', 'MuiMenu', 'MuiPopper'];
const raiseModals = (outer: object) => {
  const t = outer as ThemeLike;
  const components = { ...(t.components ?? {}) };
  for (const name of IN_PLACE) {
    components[name] = { ...(components[name] ?? {}), defaultProps: { ...(components[name]?.defaultProps ?? {}), disablePortal: true } };
  }
  return { ...outer, zIndex: { ...(t.zIndex ?? {}), drawer: 1600, modal: 1600 }, components };
};

/**
 * The wrapper every Program Planning screen renders inside.
 *
 * STYLES — Program Planning's screens are apr_template's Planning & Integration screens,
 * cloned with their markup and class names intact (`.page`, `.btn`, `.md-page`, `.rd-table`…).
 * Those classes are styled by the source's global stylesheets, copied from App
 * Rationalization's scoped copies with `.apr-app` re-scoped to `.pp-app` (`source-css/`),
 * and by each screen's own stylesheet, scoped the same way. Nothing in them can reach a
 * screen outside this wrapper. Same technique as `pages/apr/AprScreen.tsx`; cloned rather
 * than imported, because products do not import from each other's folders (§1.7).
 *
 * SCREEN LOCK — the page never scrolls; the header, tabs and filters stay put and only the
 * data scrolls, with its column headers anchored (CLAUDE.md "A screen header never
 * scrolls"). Done here once, for all fourteen cloned screens:
 *
 *   * the height is MEASURED — to the bottom of the shell's scrolling box that holds the
 *     screen (or `innerHeight − top − <main>'s bottom padding` without one) — because the
 *     shell's `<main>` is `min-height: 100vh` and grows, so `100%` resolves against nothing;
 *   * a callback ref, because the screen mounts after its loading gate;
 *   * the data region is the root's child that holds a table, re-chosen on every DOM change
 *     (tabs swap a table for cards); with no table, the element marked `data-pp-scroll`;
 *     with neither, the root scrolls under a pinned header.
 */
export function PpScreen({ children }: { children: ReactNode }) {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const ref = useCallback((node: HTMLDivElement | null) => setEl(node), []);

  useEffect(() => {
    if (!el) return;
    const main = el.closest('main');
    // The shell wraps screens in its own scrolling box with the footer below it. Measuring
    // against the window overshot that box by the footer's height (32px), so the box scrolled
    // and the whole screen — header included — slid up under the wheel. Fit the nearest
    // scrolling ancestor instead; fall back to the window when there is none.
    const scrollParent = (() => {
      for (let a = el.parentElement; a && a !== document.body; a = a.parentElement) {
        if (/(auto|scroll)/.test(getComputedStyle(a).overflowY)) return a;
      }
      return null;
    })();

    const measure = () => {
      const top = el.getBoundingClientRect().top;
      let next: number;
      if (scrollParent) {
        const box = scrollParent.getBoundingClientRect();
        const pad = parseFloat(getComputedStyle(scrollParent).paddingBottom) || 0;
        // `top` is where the screen sits now; add back any scroll so the answer is stable.
        const contentTop = top + scrollParent.scrollTop;
        next = Math.floor(box.top + scrollParent.clientTop + scrollParent.clientHeight - pad - contentTop);
      } else {
        const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
        next = Math.floor(window.innerHeight - top - inset);
      }
      next = Math.max(320, next);
      if (Math.abs(el.offsetHeight - next) >= 2) el.style.height = `${next}px`;
    };

    const assign = () => {
      const root = el.firstElementChild as HTMLElement | null;
      if (!root) return;
      root.classList.add('pp-lock');

      // The data is the biggest visible table. Its own wrapper scrolls; every element
      // between that wrapper and the root becomes a bounded column (`pp-chain`), so the
      // filters and cards that sit beside the table inside those elements stay put.
      const table = Array.from(root.querySelectorAll('table'))
        .filter((t) => t.offsetParent !== null && !t.closest('.hlp-drawer'))
        .sort((a, b) => b.rows.length - a.rows.length)[0];

      let scroller: HTMLElement | null = table?.parentElement ?? null;
      // No table on screen: a screen may name its data region with `data-pp-scroll`
      // (Integration Groupings' card list). Without one the root scrolls under a pinned header.
      if (!table) {
        scroller =
          Array.from(root.querySelectorAll<HTMLElement>('[data-pp-scroll]')).find(
            (n) => n.offsetParent !== null && !n.closest('.hlp-drawer')
          ) ?? null;
      }
      const chain: HTMLElement[] = [];
      if (scroller === root) scroller = null;
      for (let a = scroller?.parentElement ?? null; a && a !== root; a = a.parentElement) {
        const cs = getComputedStyle(a);
        const column =
          cs.display === 'block' || (cs.display === 'flex' && cs.flexDirection.startsWith('column'));
        if (!column) {
          // A row or grid ancestor cannot become a column without breaking its layout;
          // it scrolls as a whole instead, and the chain restarts above it.
          scroller = a;
          chain.length = 0;
          continue;
        }
        chain.push(a);
      }

      for (const n of Array.from(root.querySelectorAll('.pp-chain, .pp-scroll'))) {
        if (n !== scroller && !chain.includes(n as HTMLElement)) {
          n.classList.remove('pp-chain', 'pp-scroll');
        }
      }
      if (scroller) {
        chain.forEach((a) => a.classList.add('pp-chain'));
        scroller.classList.add('pp-scroll');
        root.classList.remove('pp-lock-page');
      } else {
        root.classList.add('pp-lock-page');
      }
    };

    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        assign();
        measure();
      });
    };

    schedule();
    window.addEventListener('resize', schedule);
    const ro = new ResizeObserver(schedule);
    if (main) ro.observe(main);
    if (scrollParent && scrollParent !== main) ro.observe(scrollParent);
    const mo = new MutationObserver(schedule);
    mo.observe(el, { childList: true, subtree: true });

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      ro.disconnect();
      mo.disconnect();
    };
  }, [el]);

  return (
    <WorkspaceThemeProvider baseline={false}>
      <ThemeProvider theme={raiseModals}>
        <div className="pp-app" id="pp-ws" ref={ref} style={WS_VARS}>
          {children}
        </div>
      </ThemeProvider>
    </WorkspaceThemeProvider>
  );
}
